import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Camera, Sparkles, RefreshCw, ChevronLeft, Image as ImageIcon, Loader2, FolderOpen, Download, Share2 } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { resolveImageUrl } from "../lib/imageUrl";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

/** Относительные пути из API → абсолютные (важно для Capacitor iOS). */
const resolveUrl = (u) => resolveImageUrl(u);
import { useSeo } from "../lib/seo";
import { nativeShareBlob } from "../lib/native";
import { isNativeApp } from "../lib/platform";

export default function VirtualTryOn() {
  useSeo({ title: "AI-примерка — ANGIOLINI", noindex: true });
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [params] = useSearchParams();
  const initialProductId = params.get("product") || "";

  const fileRef = useRef(null);
  const cameraRef = useRef(null);

  const [products, setProducts] = useState([]);
  const [productId, setProductId] = useState(initialProductId);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  // History lightbox: when a manager (or client) taps a saved try-on we open a
  // preview inside the same page so the Download / Share buttons can watermark
  // the image through the existing pipeline instead of opening a raw R2 link.
  const [historyItem, setHistoryItem] = useState(null);
  const [error, setError] = useState("");
  const [dlMsg, setDlMsg] = useState("");
  // Pre-seed with all 5 defaults so the picker renders 5 chips immediately
  // (instead of 1 chip → flicker to 5 once /tryon/backgrounds resolves).
  const [backgrounds, setBackgrounds] = useState([
    { id: "original",       label_ru: "На своём фото",       label_en: "Original photo" },
    { id: "studio",         label_ru: "Студия (белый фон)", label_en: "Studio" },
    { id: "boutique",       label_ru: "Бутик Angiolini",    label_en: "Luxury boutique" },
    { id: "milan_street",   label_ru: "Улочки Милана",      label_en: "Milano street" },
    { id: "marble_velvet",  label_ru: "Мрамор и бархат",    label_en: "Marble & velvet" },
    { id: "winter_city",    label_ru: "Зимний вечер",       label_en: "Winter evening" },
  ]);
  const [backgroundStyle, setBackgroundStyle] = useState("original");
  const [consentGiven, setConsentGiven] = useState(false);
  const [showConsent, setShowConsent] = useState(false);
  const [consentChecked, setConsentChecked] = useState(false);
  // Web Share API with files works on iOS Safari & Android Chrome — not desktop Chrome.
  const hasNativeShare = typeof navigator !== "undefined" && typeof navigator.share === "function";

  // Fetch the AI result as a Blob, then trigger a download. Works cross-origin
  // (R2 has Cache-Control public so browsers don't block). Falls back to opening
  // the image in a new tab if the browser blocks the blob download (rare on iOS<13).
  //
  // IMPORTANT: use credentials: "omit" — with R2_PUBLIC_BASE the image is served
  // straight from Cloudflare R2 (cross-origin), which does NOT send cookies /
  // Access-Control-Allow-Credentials. Using "include" makes the browser reject
  // the response as a CORS failure → watermarking silently fails → the download
  // ends up without the logo. "omit" is safe (public bucket, no auth needed).
  const fetchAsBlob = async (url) => {
    const r = await fetch(url, { mode: "cors", credentials: "omit", cache: "no-store" });
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    return r.blob();
  };

  // Compose the AI result with the Angiolini watermark **in the center** of the
  // image. Bottom-right was too easy to miss — center placement makes the brand
  // mark unmistakable on every shared photo.
  //
  // Quality notes:
  // - Source logo: /logo-transparent.png (1949×682 RGBA) — same file used in the app.
  // - Output: PNG (lossless) so the gold lettering & "Italian fur coats" subline stay
  //   crisp.
  // - imageSmoothingQuality = "high" → bicubic-ish resample when downscaling the logo.
  // - alpha 0.55 so the model/clothing still reads clearly underneath. Logo source
  //   is high-res, so even at 55% opacity the gold filigree is sharp.
  const stampLogoOnBlob = async (blob) => {
    try {
      const photoBitmap = await (window.createImageBitmap ? createImageBitmap(blob) : new Promise((resolve, reject) => {
        const i = new Image(); i.crossOrigin = "anonymous";
        i.onload = () => resolve(i); i.onerror = reject;
        i.src = URL.createObjectURL(blob);
      }));
      const logo = await new Promise((resolve, reject) => {
        const i = new Image();
        i.onload = () => resolve(i); i.onerror = reject;
        // High-resolution transparent PNG — same source the app uses everywhere.
        i.src = "/logo-transparent.png";
      });

      const W = photoBitmap.width || photoBitmap.naturalWidth;
      const H = photoBitmap.height || photoBitmap.naturalHeight;
      const canvas = document.createElement("canvas");
      canvas.width = W; canvas.height = H;
      const ctx = canvas.getContext("2d");
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = "high";
      ctx.drawImage(photoBitmap, 0, 0, W, H);

      // Bottom-right was easy to miss; pure center is more readable. Keep the
      // logo small (~18% width, max 360px) so it doesn't obscure the model —
      // the center position alone makes it visible enough.
      const targetW = Math.min(Math.round(W * 0.18), 360);
      const ratio = logo.naturalWidth / logo.naturalHeight;
      const lw = targetW;
      const lh = Math.round(targetW / ratio);
      const x = Math.round((W - lw) / 2);
      const y = Math.round((H - lh) / 2);

      ctx.save();
      // Very soft shadow purely for legibility on light backgrounds — small blur
      // so edges of the lettering stay crisp.
      ctx.shadowColor = "rgba(0,0,0,0.45)";
      ctx.shadowBlur = Math.max(4, W * 0.005);
      ctx.shadowOffsetX = 0; ctx.shadowOffsetY = 1;
      ctx.globalAlpha = 1.0;
      ctx.drawImage(logo, x, y, lw, lh);
      ctx.restore();

      // PNG is lossless — preserves both the photographic content and the gold
      // logo perfectly. The file is larger (~3-6 MB), acceptable for a download.
      return await new Promise((resolve) => canvas.toBlob((b) => resolve(b || blob), "image/png"));
    } catch (e) {
      // Watermarking failed (e.g. user blocked /logo-transparent.png) → keep original
      return blob;
    }
  };

  const buildFilename = (r, ext = "jpg") => {
    const slug = (r?.product_name || "angiolini-tryon").replace(/[^a-zA-Z0-9а-яА-Я]+/g, "-").slice(0, 40).replace(/^-|-$/g, "");
    const stamp = new Date().toISOString().slice(0, 16).replace(/[T:-]/g, "");
    return `${slug}-${stamp}.${ext}`;
  };

  // Detect mobile (iOS / Android). On these, we want "Save" to use the native
  // share sheet — which has "Save Image" / "Сохранить изображение", landing in
  // Photos/Gallery directly (not Files/Downloads).
  const isMobile = typeof navigator !== "undefined" && /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  const downloadResult = async (r) => {
    setDlMsg("");
    const src = resolveUrl(r.url || r.path);
    try {
      const raw = await fetchAsBlob(src);
      const blob = await stampLogoOnBlob(raw);
      const ext = (blob.type.split("/")[1] || "jpg").split(";")[0];
      const filename = buildFilename(r, ext);
      const file = new File([blob], filename, { type: blob.type });

      // 0) Нативная сборка: пишем файл в кэш и открываем системное меню —
      //    оттуда «Сохранить в Галерею» и отправка в WhatsApp.
      if (isNativeApp()) {
        try {
          const shared = await nativeShareBlob(blob, filename, "ANGIOLINI VIP CLUB");
          if (shared) {
            setDlMsg("Выберите «Сохранить» или «Поделиться» 📸");
            setTimeout(() => setDlMsg(""), 3500);
            return;
          }
        } catch (nativeErr) {
          if (nativeErr?.message === "Share canceled") return;
        }
      }

      // 1) Mobile + Web Share API w/ files → open native share sheet so the
      //    user can tap "Save Image" → lands in Photos (iOS) / Gallery (Android).
      //    This is the ONLY programmatic path for web pages to write into the
      //    Photos library on iOS Safari.
      if (isMobile && navigator.canShare && navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({ files: [file], title: "Angiolini" });
          setDlMsg("Нажмите «Сохранить изображение» 📸");
          setTimeout(() => setDlMsg(""), 3500);
          return;
        } catch (shareErr) {
          // AbortError = user cancelled → stop; any other error → fall through to blob-download.
          if (shareErr?.name === "AbortError") return;
        }
      }

      // 2) Desktop / older mobile → classic blob download (lands in Downloads).
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1500);
      setDlMsg("Фото сохранено в Загрузках 📥");
      setTimeout(() => setDlMsg(""), 3500);
    } catch (e) {
      // Last-resort fallback: open in new tab so user can long-press → Save
      window.open(src, "_blank");
      setDlMsg("Откройте картинку в новой вкладке и нажмите «Сохранить»");
      setTimeout(() => setDlMsg(""), 5000);
    }
  };

  const shareResult = async (r) => {
    setDlMsg("");
    const src = resolveUrl(r.url || r.path);
    const text = `Моя примерка в Angiolini · ${r.product_name || ""}`;
    try {
      const raw = await fetchAsBlob(src);
      const blob = await stampLogoOnBlob(raw);
      const ext = (blob.type.split("/")[1] || "jpg").split(";")[0];
      const file = new File([blob], buildFilename(r, ext), { type: blob.type });
      // navigator.canShare check — files may not be supported on older browsers
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: "Angiolini", text });
      } else {
        await navigator.share({ title: "Angiolini", text, url: src });
      }
      setDlMsg("Готово ✨");
      setTimeout(() => setDlMsg(""), 2500);
    } catch (e) {
      // AbortError = user cancelled the share sheet — that's fine, no message
      if (e?.name !== "AbortError") {
        // Fallback: open WhatsApp deep link
        const wa = `https://wa.me/?text=${encodeURIComponent(text + " " + src)}`;
        window.open(wa, "_blank");
      }
    }
  };

  useEffect(() => {
    axios.get(`${API}/products`).then(r => {
      const list = r.data || [];
      setProducts(list);
      // If no product selected (no ?product= query, no prior choice), auto-pick the first one
      // so the user only needs to upload a photo to enable Generate.
      setProductId(prev => prev || (list[0]?.id ?? ""));
    }).catch(() => {});
    axios.get(`${API}/tryon/history`, { withCredentials: true }).then(r => setHistory(r.data || [])).catch(() => {});
    axios.get(`${API}/tryon/backgrounds`).then(r => {
      if (Array.isArray(r.data) && r.data.length) setBackgrounds(r.data);
    }).catch(() => {});
    axios.get(`${API}/me/tryon-consent`, { withCredentials: true })
      .then(r => setConsentGiven(Boolean(r.data?.given))).catch(() => {});
  }, []);

  const selectedProduct = products.find(p => p.id === productId);

  const onPickFile = (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setError("Пожалуйста, выберите изображение");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      setError("Файл слишком большой (макс. 8 МБ)");
      return;
    }
    setError("");
    setPhotoFile(file);
    setPhotoPreview(URL.createObjectURL(file));
  };

  // Фото с телефона — 4–8 МБ: загрузка съедала больше времени, чем сама
  // генерация. Сжимаем до 1280px JPEG прямо в браузере (качество для примерки
  // не страдает, вес падает в 10–20 раз).
  const compressPhoto = async (file) => {
    try {
      const bitmap = await createImageBitmap(file);
      const scale = Math.min(1, 1280 / Math.max(bitmap.width, bitmap.height));
      const w = Math.round(bitmap.width * scale);
      const h = Math.round(bitmap.height * scale);
      const canvas = document.createElement("canvas");
      canvas.width = w; canvas.height = h;
      canvas.getContext("2d").drawImage(bitmap, 0, 0, w, h);
      const blob = await new Promise(res => canvas.toBlob(res, "image/jpeg", 0.85));
      if (!blob || blob.size >= file.size) return file;
      return new File([blob], "photo.jpg", { type: "image/jpeg" });
    } catch {
      return file;
    }
  };

  const handleGenerate = async () => {
    if (!photoFile || !productId) {
      setError("Загрузите фото и выберите товар");
      return;
    }
    if (!consentGiven) {
      setShowConsent(true);
      return;
    }
    setError("");
    setGenerating(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("photo", await compressPhoto(photoFile));
      fd.append("product_id", productId);
      fd.append("background_style", backgroundStyle);
      const r = await axios.post(`${API}/tryon/generate`, fd, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });
      setResult(r.data);
      // refresh history
      axios.get(`${API}/tryon/history`, { withCredentials: true }).then(rr => setHistory(rr.data || [])).catch(() => {});
    } catch (e) {
      setError(e?.response?.data?.detail || "Не удалось сгенерировать. Попробуйте другое фото.");
    } finally {
      setGenerating(false);
    }
  };

  const reset = () => {
    setResult(null);
    setPhotoFile(null);
    setPhotoPreview(null);
    if (fileRef.current) fileRef.current.value = "";
    if (cameraRef.current) cameraRef.current.value = "";
  };

  return (
    <div data-testid="virtual-tryon-page" style={{ background: "var(--ang-bg)", minHeight: "100vh", paddingBottom: "30px" }}>
      {/* Header */}
      <div style={{ padding: "52px 20px 18px", display: "flex", alignItems: "center", gap: "12px" }}>
        <button onClick={() => navigate(-1)} style={{ background: "none", border: "none", color: "var(--ang-text-muted)", cursor: "pointer" }}>
          <ChevronLeft size={20} />
        </button>
        <div>
          <h1 style={{ fontFamily: "Cormorant Garamond", fontSize: "26px", color: "var(--ang-text)", fontWeight: 400, margin: 0, display: "flex", alignItems: "center", gap: "10px" }}>
            AI-примерочная <Sparkles size={18} style={{ color: "var(--ang-gold)" }} />
          </h1>
          <div style={{ fontFamily: "Manrope", fontSize: "11px", color: "var(--ang-text-faint)", marginTop: "2px" }}>
            Загрузите своё фото и увидите, как сядет выбранное изделие
          </div>
        </div>
      </div>

      {/* Step 1: pick product */}
      <div style={{ padding: "0 16px 14px" }}>
        <div style={{ fontFamily: "Manrope", fontSize: "10px", letterSpacing: "0.18em", textTransform: "uppercase", color: "var(--ang-gold-strong)", marginBottom: "10px" }}>1. Выберите изделие</div>
        <div style={{ display: "flex", gap: "10px", overflowX: "auto", paddingBottom: "6px" }}>
          {products.map(p => (
            <button key={p.id} data-testid={`tryon-product-${p.id}`} onClick={() => setProductId(p.id)}
              style={{
                flexShrink: 0, width: "110px",
                background: productId === p.id ? "var(--ang-gold-soft)" : "var(--ang-surface-2)",
                border: `1px solid ${productId === p.id ? "var(--ang-gold)" : "var(--ang-border)"}`,
                borderRadius: "6px", padding: "6px", cursor: "pointer", textAlign: "left", transition: "all 0.2s",
              }}>
              <img src={resolveImageUrl(p.images?.[0], { w: 600 })} alt={p.name}
                style={{ width: "100%", height: "120px", objectFit: "cover", borderRadius: "3px" }} />
              <div style={{ fontFamily: "Manrope", fontSize: "10px", color: productId === p.id ? "var(--ang-gold)" : "var(--ang-text-soft)", marginTop: "6px", lineHeight: 1.25,
                display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                {p.name}
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Step 2: upload photo */}
      <div style={{ padding: "0 16px 14px" }}>
        <div style={{ fontFamily: "Manrope", fontSize: "10px", letterSpacing: "0.18em", textTransform: "uppercase", color: "var(--ang-gold-strong)", marginBottom: "10px" }}>2. Загрузите своё фото</div>

        {/* Hidden inputs: one for gallery, one for camera */}
        <input ref={fileRef} type="file" accept="image/*" style={{ display: "none" }}
          data-testid="tryon-file-input"
          onChange={e => onPickFile(e.target.files?.[0])} />
        <input ref={cameraRef} type="file" accept="image/*" capture="user" style={{ display: "none" }}
          data-testid="tryon-camera-input"
          onChange={e => onPickFile(e.target.files?.[0])} />

        {/* Preview area or empty hint */}
        {photoPreview ? (
          <div style={{ background: "var(--ang-surface-2)", borderRadius: "8px", overflow: "hidden", border: "1px solid var(--ang-gold-soft)" }}>
            <img src={photoPreview} alt="Ваше фото" style={{ width: "100%", maxHeight: "320px", objectFit: "contain", display: "block" }} />
            <div style={{ display: "flex", justifyContent: "center", padding: "8px", gap: "10px" }}>
              <button onClick={() => fileRef.current?.click()}
                style={{ flex: 1, background: "transparent", border: "1px solid var(--ang-border)", color: "var(--ang-text-soft)", borderRadius: "4px", padding: "8px", fontFamily: "Manrope", fontSize: "11px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: "6px" }}>
                <FolderOpen size={13} /> Заменить
              </button>
            </div>
          </div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
            <button data-testid="tryon-pick-gallery-btn" onClick={() => fileRef.current?.click()}
              style={{
                background: "var(--ang-surface-2)", border: "1.5px dashed var(--ang-gold-line)",
                borderRadius: "8px", padding: "22px 12px", display: "flex", flexDirection: "column",
                alignItems: "center", justifyContent: "center", gap: "8px", cursor: "pointer",
                color: "var(--ang-text-soft)", fontFamily: "Manrope", fontSize: "12px",
              }}>
              <FolderOpen size={26} style={{ color: "var(--ang-gold)" }} />
              <div style={{ fontWeight: 600 }}>Из галереи</div>
              <div style={{ fontSize: "10px", color: "var(--ang-text-faint)" }}>Выбрать из устройства</div>
            </button>
            <button data-testid="tryon-pick-camera-btn" onClick={() => cameraRef.current?.click()}
              style={{
                background: "var(--ang-surface-2)", border: "1.5px dashed var(--ang-gold-line)",
                borderRadius: "8px", padding: "22px 12px", display: "flex", flexDirection: "column",
                alignItems: "center", justifyContent: "center", gap: "8px", cursor: "pointer",
                color: "var(--ang-text-soft)", fontFamily: "Manrope", fontSize: "12px",
              }}>
              <Camera size={26} style={{ color: "var(--ang-gold)" }} />
              <div style={{ fontWeight: 600 }}>Сделать селфи</div>
              <div style={{ fontSize: "10px", color: "var(--ang-text-faint)" }}>Включить камеру</div>
            </button>
          </div>
        )}
      </div>

      {error && <div style={{ margin: "0 16px 12px", padding: "10px 14px", background: "rgba(225,29,42,0.12)", border: "1px solid rgba(225,29,42,0.3)", borderRadius: "6px", color: "#ff6b6b", fontFamily: "Manrope", fontSize: "12px" }}>{error}</div>}

      {/* Step 2.5: background style picker */}
      {!result && (
        <div data-testid="tryon-bg-picker" style={{ padding: "0 16px 14px" }}>
          <div style={{ fontFamily: "Manrope", fontSize: "10px", letterSpacing: "0.18em", textTransform: "uppercase", color: "var(--ang-gold-strong)", marginBottom: "10px" }}>
            Фон примерки
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))", gap: "10px" }}>
            {backgrounds.map(bg => {
              const active = backgroundStyle === bg.id;
              return (
                <button
                  key={bg.id}
                  data-testid={`tryon-bg-${bg.id}`}
                  onClick={() => setBackgroundStyle(bg.id)}
                  disabled={generating}
                  style={{
                    background: active ? "var(--ang-gold-soft)" : "var(--ang-surface-2)",
                    border: active ? "1px solid var(--ang-gold)" : "1px solid var(--ang-border)",
                    borderRadius: "6px",
                    padding: "10px 8px",
                    cursor: generating ? "not-allowed" : "pointer",
                    color: active ? "var(--ang-gold)" : "var(--ang-text-soft)",
                    fontFamily: "Manrope",
                    fontSize: "11px",
                    fontWeight: active ? 700 : 500,
                    textAlign: "center",
                    lineHeight: 1.3,
                    transition: "all 0.15s",
                  }}
                >
                  {bg.label_ru}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Step 3: generate */}
      <div style={{ padding: "0 16px 6px" }}>
        <button data-testid="tryon-generate-btn" onClick={handleGenerate} disabled={generating || !photoFile || !productId}
          style={{
            width: "100%",
            background: generating || !photoFile || !productId ? "var(--ang-gold-line)" : "linear-gradient(135deg,var(--ang-gold),var(--ang-gold-2))",
            color: "var(--ang-bg)", border: "none", borderRadius: "4px", padding: "16px",
            fontFamily: "Manrope", fontWeight: 700, fontSize: "12px", letterSpacing: "0.14em",
            textTransform: "uppercase", cursor: generating ? "wait" : "pointer",
            display: "flex", alignItems: "center", justifyContent: "center", gap: "10px",
          }}>
          {generating ? (
            <>
              <Loader2 size={16} style={{ animation: "spin 1s linear infinite" }} />
              Генерируем образ... 15-30 сек
            </>
          ) : (
            <>
              <Sparkles size={16} />
              Сгенерировать виртуальную примерку
            </>
          )}
        </button>
        {!generating && (!photoFile || !productId) && (
          <div data-testid="tryon-hint" style={{ marginTop: "8px", textAlign: "center", fontFamily: "Manrope", fontSize: "11px", color: "var(--ang-gold-strong)" }}>
            {!productId ? "Выберите изделие выше" : "Загрузите своё фото"}
          </div>
        )}
        <style>{`@keyframes spin{from{transform:rotate(0)}to{transform:rotate(360deg)}}`}</style>
      </div>

      {/* Result */}
      <AnimatePresence mode="wait">
        {result && (
          <motion.div key="result" initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
            style={{ padding: "0 16px 18px" }}>
            <div style={{ fontFamily: "Manrope", fontSize: "10px", letterSpacing: "0.18em", textTransform: "uppercase", color: "var(--ang-gold-strong)", marginBottom: "10px" }}>Результат</div>
            <div style={{ background: "var(--ang-surface-2)", border: "1px solid var(--ang-gold-line)", borderRadius: "8px", overflow: "hidden" }}>
              <img data-testid="tryon-result-image" src={resolveUrl(result.url || result.path)} alt="AI try-on"
                style={{ width: "100%", display: "block" }} />
              <div style={{ padding: "14px", display: "flex", flexDirection: "column", gap: "10px" }}>
                <div style={{ fontFamily: "Cormorant Garamond", fontSize: "16px", color: "var(--ang-text)" }}>{result.product_name}</div>
                <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                  <button
                    data-testid="tryon-download-btn"
                    onClick={() => downloadResult(result)}
                    style={{ flex: "1 1 auto", minWidth: "110px", background: "linear-gradient(135deg,var(--ang-gold),var(--ang-gold-2))", color: "var(--ang-bg)", border: "none", borderRadius: "4px", padding: "10px 12px", cursor: "pointer", fontFamily: "Manrope", fontSize: "11px", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", display: "flex", alignItems: "center", justifyContent: "center", gap: "6px" }}>
                    <Download size={13} /> Скачать
                  </button>
                  {hasNativeShare && (
                    <button
                      data-testid="tryon-share-btn"
                      onClick={() => shareResult(result)}
                      title="Поделиться с друзьями или консультантом"
                      style={{ flex: "1 1 auto", minWidth: "110px", background: "var(--ang-gold-soft)", color: "var(--ang-gold)", border: "1px solid var(--ang-gold-line)", borderRadius: "4px", padding: "10px 12px", cursor: "pointer", fontFamily: "Manrope", fontSize: "11px", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", display: "flex", alignItems: "center", justifyContent: "center", gap: "6px" }}>
                      <Share2 size={13} /> Поделиться
                    </button>
                  )}
                  <button onClick={() => navigate(`/app/catalog/${result.product_id}`)}
                    data-testid="tryon-to-product-btn"
                    style={{ flex: "1 1 auto", minWidth: "100px", background: "transparent", color: "var(--ang-text-soft)", border: "1px solid var(--ang-border-strong)", borderRadius: "4px", padding: "10px 12px", cursor: "pointer", fontFamily: "Manrope", fontSize: "11px", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase" }}>
                    К товару
                  </button>
                  <button onClick={reset} title="Сгенерировать ещё раз"
                    style={{ background: "var(--ang-surface)", color: "var(--ang-text-soft)", border: "1px solid var(--ang-border)", borderRadius: "4px", padding: "10px 12px", cursor: "pointer", display: "flex", alignItems: "center", gap: "4px", fontFamily: "Manrope", fontSize: "11px" }}>
                    <RefreshCw size={12} /> Ещё
                  </button>
                </div>
                {dlMsg && (
                  <div data-testid="tryon-dl-message" style={{ fontFamily: "Manrope", fontSize: "11px", color: "var(--ang-gold-strong)", textAlign: "center", marginTop: "2px" }}>
                    {dlMsg}
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* History */}
      {history.length > 0 && (
        <div style={{ padding: "8px 16px 16px" }}>
          <div style={{ fontFamily: "Manrope", fontSize: "10px", letterSpacing: "0.18em", textTransform: "uppercase", color: "var(--ang-text-faint)", marginBottom: "10px", display: "flex", alignItems: "center", gap: "6px" }}>
            <ImageIcon size={11} /> История примерок
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "8px" }}>
            {history.slice(0, 9).map(h => (
              <button key={h.id} data-testid={`history-item-${h.id}`}
                onClick={() => setHistoryItem(h)}
                style={{ display: "block", borderRadius: "4px", overflow: "hidden", background: "var(--ang-surface)",
                          aspectRatio: "1/1", border: "none", padding: 0, cursor: "pointer" }}>
                <img src={resolveUrl(h.url || h.path)} alt="history" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              </button>
            ))}
          </div>
        </div>
      )}

      {/* History lightbox — reuses downloadResult / shareResult so the saved
          image gets the exact same Angiolini watermark treatment as a fresh
          try-on. Fixes the "download opens raw link, no logo" bug. */}
      <AnimatePresence>
        {historyItem && (
          <motion.div
            data-testid="history-lightbox"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setHistoryItem(null)}
            style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.92)", zIndex: 260,
                      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                      padding: "16px" }}>
            <div onClick={e => e.stopPropagation()}
              style={{ maxWidth: "560px", width: "100%", display: "flex", flexDirection: "column",
                        gap: "12px", alignItems: "center" }}>
              <img src={resolveUrl(historyItem.url || historyItem.path)} alt=""
                style={{ maxWidth: "100%", maxHeight: "70vh", borderRadius: "8px",
                          boxShadow: "0 8px 40px rgba(0,0,0,0.6)" }} />
              {dlMsg && (
                <div style={{ color: "var(--ang-gold-2)", fontFamily: "Manrope", fontSize: "12px", textAlign: "center" }}>
                  {dlMsg}
                </div>
              )}
              <div style={{ display: "flex", gap: "8px", width: "100%" }}>
                <button data-testid="history-download-btn"
                  onClick={() => downloadResult(historyItem)}
                  style={{ flex: 1, background: "linear-gradient(135deg,var(--ang-gold),var(--ang-gold-2))", color: "var(--ang-bg)",
                            border: "none", borderRadius: "6px", padding: "12px", cursor: "pointer",
                            fontFamily: "Manrope", fontSize: "12px", fontWeight: 700, letterSpacing: "0.08em",
                            textTransform: "uppercase", display: "inline-flex", alignItems: "center",
                            justifyContent: "center", gap: "6px" }}>
                  <Download size={14} /> Скачать
                </button>
                {hasNativeShare && (
                  <button data-testid="history-share-btn"
                    onClick={() => shareResult(historyItem)}
                    style={{ flex: 1, background: "transparent", color: "var(--ang-gold-2)", border: "1px solid var(--ang-gold)",
                              borderRadius: "6px", padding: "12px", cursor: "pointer",
                              fontFamily: "Manrope", fontSize: "12px", fontWeight: 700, letterSpacing: "0.08em",
                              textTransform: "uppercase", display: "inline-flex", alignItems: "center",
                              justifyContent: "center", gap: "6px" }}>
                    <Share2 size={14} /> Поделиться
                  </button>
                )}
              </div>
              <button data-testid="history-delete-btn"
                onClick={async () => {
                  if (!window.confirm("Удалить эту примерку безвозвратно?")) return;
                  try {
                    await axios.delete(`${API}/tryon/history/${historyItem.id}`, { withCredentials: true });
                    setHistory(prev => prev.filter(h => h.id !== historyItem.id));
                    setHistoryItem(null);
                  } catch {
                    setError("Не удалось удалить примерку");
                  }
                }}
                style={{ background: "transparent", color: "#C45B4A", border: "none",
                          padding: "8px 12px", cursor: "pointer", fontFamily: "Manrope", fontSize: "12px" }}>
                Удалить примерку
              </button>
              <button data-testid="history-close-btn"
                onClick={() => setHistoryItem(null)}
                style={{ background: "transparent", color: "var(--ang-text-muted)", border: "none",
                          padding: "8px 12px", cursor: "pointer", fontFamily: "Manrope", fontSize: "12px" }}>
                Закрыть
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {showConsent && (
        <div data-testid="tryon-consent-modal"
          style={{ position: "fixed", inset: 0, zIndex: 90, background: "rgba(10,10,10,0.8)",
            display: "flex", alignItems: "center", justifyContent: "center", padding: "20px" }}>
          <div style={{ background: "var(--ang-bg)", border: "1px solid var(--ang-card-border)",
            borderRadius: "12px", padding: "22px", maxWidth: "420px", width: "100%" }}>
            <div style={{ fontFamily: "Cormorant Garamond", fontSize: "22px", color: "var(--ang-text)", marginBottom: "12px" }}>
              Согласие на обработку фотографии
            </div>
            <div style={{ fontFamily: "Manrope", fontSize: "12.5px", color: "var(--ang-text-soft)", lineHeight: 1.75 }}>
              Ваше фото будет передано сервису <b>Google Gemini API</b> (через LLM-прокси <b>Emergent Integrations</b>)
              исключительно для того, чтобы сгенерировать изображение выбранной вещи на вас.
              <br /><br />
              Распознавание лица, биометрическая идентификация и построение биометрических шаблонов
              не выполняются. Исходное фото не сохраняется на наших серверах: оно обрабатывается в
              оперативной памяти и удаляется сразу после генерации. Сохраняется только результат
              примерки — в вашем личном кабинете, и вы можете удалить его в любой момент или вместе с аккаунтом.
              <br /><br />
              Подробнее — в{" "}
              <a href="/legal?tab=privacy" target="_blank" rel="noopener noreferrer"
                style={{ color: "var(--ang-gold-2)" }}>Политике конфиденциальности</a>, раздел
              «Фотографии для AI-примерки».
            </div>
            <label style={{ display: "flex", gap: "10px", alignItems: "flex-start", margin: "16px 0",
              fontFamily: "Manrope", fontSize: "12px", color: "var(--ang-text)" }}>
              <input data-testid="tryon-consent-checkbox" type="checkbox" checked={consentChecked}
                onChange={e => setConsentChecked(e.target.checked)} style={{ marginTop: "3px" }} />
              Я соглашаюсь на передачу моей фотографии указанным обработчикам для создания примерки
            </label>
            <div style={{ display: "flex", gap: "10px" }}>
              <button data-testid="tryon-consent-accept" disabled={!consentChecked}
                onClick={async () => {
                  try {
                    await axios.post(`${API}/me/tryon-consent`, {}, { withCredentials: true });
                    setConsentGiven(true);
                    setShowConsent(false);
                    setTimeout(handleGenerate, 0);
                  } catch {
                    setError("Не удалось сохранить согласие. Попробуйте ещё раз.");
                  }
                }}
                style={{ flex: 1, background: consentChecked ? "var(--ang-gold)" : "var(--ang-card-border)",
                  color: consentChecked ? "#1A1A1A" : "var(--ang-text-faint)", border: "none", borderRadius: "6px",
                  padding: "13px", fontFamily: "Manrope", fontSize: "12px", fontWeight: 800,
                  letterSpacing: "0.06em", textTransform: "uppercase", cursor: consentChecked ? "pointer" : "not-allowed" }}>
                Согласен и продолжить
              </button>
              <button data-testid="tryon-consent-decline" onClick={() => setShowConsent(false)}
                style={{ background: "transparent", color: "var(--ang-text-muted)", border: "1px solid var(--ang-card-border)",
                  borderRadius: "6px", padding: "13px 16px", fontFamily: "Manrope", fontSize: "12px", cursor: "pointer" }}>
                Отмена
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
